"use client";

import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { LongShortPriceChart } from "./charts";
import { DATA_REFRESHED_EVENT } from "@/lib/data-refresh";
import { FreshnessTag, Panel } from "./ui";
import type { LongShortPriceData } from "@/lib/types";

const COINGLASS_POLL_INTERVAL_MS = 60_000;

function formatContracts(value: number) {
  return new Intl.NumberFormat("en", {
    notation: "compact",
    maximumFractionDigits: 1,
    signDisplay: "exceptZero"
  }).format(value);
}

export function LongShortWidget() {
  const [data, setData] = useState<LongShortPriceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = data?.points.at(-1);
  const positioningStale = data?.cache_status === "stale";
  const displayedPrice = data?.current_price ?? latest?.price;

  const load = useCallback(async (foreground = false) => {
    if (foreground) setLoading(true);
    try {
      const response = await fetch("/api/coinglass/long-short?interval=4h&limit=180", {
        cache: "no-store",
        credentials: "include"
      });
      if (!response.ok) throw new Error("Long/short positioning is temporarily unavailable.");
      const payload = (await response.json()) as LongShortPriceData;
      if (payload.interval !== "4h" || !Array.isArray(payload.points) || payload.points.length === 0) {
        throw new Error("CoinGlass returned no validated 4h observations.");
      }
      setData(payload);
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Long/short positioning is temporarily unavailable.");
    } finally {
      if (foreground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(true);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void load();
    }, COINGLASS_POLL_INTERVAL_MS);
    const reload = () => void load();
    window.addEventListener(DATA_REFRESHED_EVENT, reload);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(DATA_REFRESHED_EVENT, reload);
    };
  }, [load]);

  return (
    <section id="long-short" aria-labelledby="long-short-heading" className="space-y-4">
      <Panel className="px-5 py-6 md:px-7 md:py-7">
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--hc-muted)]">Positioning</p>
            <h2 id="long-short-heading" className="mt-2 text-2xl font-semibold tracking-[-0.035em] text-[var(--hc-text)]">BTC Net Long / Net Short</h2>
            <p className="mt-2 max-w-4xl text-sm leading-6 text-[var(--hc-muted)]">CoinGlass V2 net positioning aligned with CoinGlass Binance perpetual OHLC. Fixed 4h timeframe; cumulative change over the displayed window, not an account ratio.</p>
          </div>
          <div className="flex items-center gap-2">
            {data ? <FreshnessTag status={data.cache_status ?? "live"} source="CoinGlass V2" collectedAt={data.cache_collected_at ?? data.collected_at} /> : null}
            <button
              type="button"
              onClick={() => void load(true)}
              disabled={loading}
              aria-label="Refresh long short chart"
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-1)] text-[var(--hc-muted)] transition-colors hover:border-[var(--hc-border-strong)] hover:text-[var(--hc-text)] disabled:cursor-wait disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div aria-live="polite" aria-busy={loading}>
          {positioningStale ? (
            <div role="status" className="mb-4 rounded-md border border-amber-400/40 bg-amber-400/5 px-4 py-3 text-sm leading-6 text-amber-200">
              CoinGlass positioning is stale. Net Long, Net Short, and the aligned chart are the last validated snapshot; the BTC price card remains live from Binance.
            </div>
          ) : null}
          {data && latest ? (
            <>
              <div className="mb-6 grid gap-2.5 sm:grid-cols-3">
                <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--hc-muted)]">Net Long</p>
                  <p className="mt-1.5 text-lg font-semibold text-emerald-400">{formatContracts(latest.net_long_contracts)}</p>
                </div>
                <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--hc-muted)]">Net Short</p>
                  <p className="mt-1.5 text-lg font-semibold text-rose-400">{formatContracts(latest.net_short_contracts)}</p>
                </div>
                <div className="rounded-md border border-[var(--hc-border)] bg-[var(--hc-surface-2)] px-4 py-3.5">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--hc-muted)]">BTC Perpetual · {data.current_price ? "Live" : "Snapshot"}</p>
                  <p className="mt-1.5 text-lg font-semibold text-[var(--hc-text)]">${displayedPrice?.toLocaleString("en", { maximumFractionDigits: 0 })}</p>
                </div>
              </div>
              <LongShortPriceChart
                points={data.points}
                livePrice={data.current_price}
                livePriceAt={data.price_collected_at}
                height={520}
              />
            </>
          ) : null}
          {loading && !data ? <div className="h-[620px] animate-pulse rounded-md bg-[var(--hc-surface-2)]" /> : null}
          {error ? (
            <div className="rounded-md border border-rose-400/40 bg-rose-400/5 px-4 py-3 text-sm text-rose-300">{error}</div>
          ) : null}
        </div>
      </Panel>
    </section>
  );
}
